<template>
  <div class="docs-listing__container">
    <div class="filter">
      <div v-if="view" class="filter__container">
        <h2 class="filter__title">
          {{ view.filterTitle() }}
        </h2>
        <CustomListMenu
          v-if="view.customList && showCustomListMenu"
          :list="view.customList"
          :listsView="props.listsView"
        />
        <div
          v-if="showDocSort && view.docs().isDocSortVisible()"
          class="filter__box"
        >
          <DocsSort
            :view="
              // `isDocSortVisible` only returns true in `DocsView`.
              view.docs() as DocsView
            "
          />
        </div>
      </div>
    </div>

    <p v-if="view.description()" class="docs-listing__description">
      {{ view.description() }}
    </p>

    <!-- Main data -->
    <div v-if="view.dataLength" class="docs-listing__list">
      <ListCard
        v-for="doc in view.docs().filteredDocs"
        :key="doc.id"
        :view="view"
        :doc="doc"
        @click="trackDocView($event, doc)"
      />
    </div>

    <div v-if="view && !view.dataLength">
      <div v-if="isAnyDocTypeUnchecked()" class="text">No items found.</div>
      <CustomListEmpty
        v-else-if="view.customList?.doc_type === 'custom_list'"
      />
      <div v-else class="text">
        {{ view.notFoundMessage() }}
      </div>
    </div>

    <DocsSortMobile
      v-if="view.docs().isDocSortVisible()"
      :view="
        // `isDocSortVisible` only returns true in `DocsView`.
        view.docs() as DocsView
      "
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  ref,
} from 'vue'

import CustomListMenu from '@/app/components/custom.lists/CustomListMenu.vue'
import CustomListEmpty from '@/app/components/custom.lists/CustomListEmpty.vue'
import ListCard from '@/app/components/listing/ListCard.vue'
import DocsSort from '@/app/components/listing/DocsSort.vue'
import DocsSortMobile from '@/app/components/listing/DocsSort.mobile.vue'

import { onBeforeRouteLeave, useRouter } from 'vue-router'

import { CustomListsView, isEditableCustomList } from '@/models/custom.lists'
import type { ListView } from '@/models/docs.list'
import { DocsScroll } from '@/models/docs.scroll'
import { Mixpanel } from '@/analytics/mixpanel'
import { assertNotNull } from '@/helpers/typing'
import type { ListItem } from '@/models/interfaces'
import { DocsView } from '@/models/doc'
import { Sentry } from '@/services/sentry'
import { playerSession } from '@/services/player'

const props = withDefaults(
  defineProps<{
    view: ListView
    showDocSort?: boolean
    listsView?: CustomListsView
  }>(),
  { showDocSort: true },
)

const router = useRouter()

const docsScroll = ref<DocsScroll | null>(null)

const lastScrollPosition = ref<number | null>(null)

onMounted(() => {
  if (props.view) {
    props.view.trackSearchResultsBatch(props.view.docs().filteredDocs)
    docsScroll.value = new DocsScroll(props.view, (docs) => {
      props.view.trackSearchResultsBatch(docs)
    })
  }
})

onActivated(async () => {
  // Start the event listeners if they haven't started already.
  if (!docsScroll.value?.isListening) {
    docsScroll.value?.initEventListeners()
  }

  // If there's a stored scroll position, restore it.
  if (lastScrollPosition.value) {
    await nextTick()
    appEl().scrollTop = lastScrollPosition.value
  }
})

onBeforeRouteLeave(() => {
  // Store the scroll position when going to a new page, so it can be
  // restored later when this component is restored.
  //
  // We do this `onBeforeRouteLeave` instead of `onDeactivated` because
  // `onDeactivated` only triggers after the component has already left
  // the DOM, so we can't get the scroll position.
  lastScrollPosition.value = appEl().scrollTop
})

onDeactivated(() => {
  docsScroll.value?.removeScrollEventListener()
})

onBeforeUnmount(() => {
  if (docsScroll.value) {
    docsScroll.value.removeScrollEventListener()
  }
})

/**
 * Get the app container element.
 */
function appEl(): HTMLElement {
  return assertNotNull(document.getElementById('app'))
}

function trackDocView(event: Event, doc: ListItem): void {
  try {
    playerSession.value.setNextArrival('card')
    Mixpanel.trackDocView(
      event,
      props.view.filterTitle(),
      router.currentRoute.value.fullPath,
      doc,
      props.view.docViewEventProperties(doc),
    )
  } catch (error: any) {
    // Unexpected error in the Mixpanel tracking
    Sentry.captureException(error)
  }
}

/**
 * Return whether any type is unchecked in the document-type filter, which
 * means an empty list may not really be empty.
 */
function isAnyDocTypeUnchecked(): boolean {
  const docs = props.view.docs()
  return docs instanceof DocsView && docs.isAnyDocTypeUnchecked
}

/**
 * Return whether or not the current page is a non-standard Custom List listing page.
 */
const showCustomListMenu = computed(() =>
  isEditableCustomList(props.view.customList),
)
</script>

<style lang="scss">
.docs-listing__description {
  margin-top: -12px;
  margin-bottom: 24px;
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  letter-spacing: -0.02em;
  color: $c-black-12;
  @media only screen and (max-width: $w-tablet-s) {
    margin-top: 0;
    margin-bottom: 16px;
    font-size: 16px;
  }
}

.night-mode .docs-listing__description {
  color: $c-dark-4;
}

.docs-listing__list {
  --docs-listing-columns-desktop: 4;
  display: grid;
  grid-template-columns: repeat(var(--docs-listing-columns-desktop), 1fr);
  gap: 24px;
  @media only screen and (max-width: $w-tablet-s) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  @media only screen and (max-width: $w-tablet-s) {
    gap: 13px;
  }
}
</style>
